/**
 * Utility functions for handling images
 */

/**
 * Cria uma URL de avatar de fallback usando o serviço UI Avatars
 *
 * @param name O nome a ser usado no avatar
 * @param size O tamanho do avatar em pixels
 * @returns Uma URL para um avatar gerado
 */
export const createFallbackAvatar = (name: string = 'User', size: number = 200): string => {
  const initials = name
    .split(' ')
    .map((n) => n[0]?.toUpperCase())
    .slice(0, 2)
    .join('');
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&size=${size}&color=ffffff&bold=true`;
};

/**
 * Converte um File em uma Data URL
 *
 * @param file O arquivo a ser convertido
 * @returns Promise que resolve para a data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Retorna a URL proxied se a imagem estiver no Supabase Storage público
 *
 * @param url URL original
 * @returns URL proxied se aplicável, senão a URL original
 */
export const getProxiedImageUrl = (url: string | null): string => {
  if (!url) return '';
  
  try {
    // For Supabase storage URLs, use the image-proxy function
    const supabasePublicPath = '/storage/v1/object/public/';
    if (url && url.includes(supabasePublicPath)) {
      const pathMatch = url.match(/\/public\/(.+)$/);
      if (pathMatch && pathMatch[1]) {
        const encodedPath = encodeURIComponent(pathMatch[1]);
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        
        // Check if the URL is already proxied
        if (url.includes('/functions/v1/image-proxy')) {
          return url;
        }
        
        return `${baseUrl}/functions/v1/image-proxy?path=${encodedPath}`;
      }
    }
    
    // If it's already a full URL, return it as is
    if (url.startsWith('http')) {
      return url;
    }
    
    // If it's a relative URL, make it absolute
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`;
    }
    
    return url;
  } catch (err) {
    console.error('Error processing image URL:', err);
    return url; // Return original URL as fallback
  }
};

/**
 * Mapeia extensões de arquivos para tipos MIME comuns
 */
export const mimeTypes: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

/**
 * Tenta determinar o tipo MIME de um arquivo de imagem.
 *
 * @param file O arquivo a ser analisado
 * @returns O tipo MIME detectado
 */
export const getMimeTypeFromFile = (file: File): string => {
  if (!file || !file.name) return 'application/octet-stream';

  // Primeiro, verificar se o arquivo tem um tipo MIME válido
  if (file.type && file.type.startsWith('image/')) {
    console.log('Usando tipo MIME do arquivo:', file.type);
    return file.type;
  }

  // Se não tem tipo MIME válido, tentar determinar pela extensão
  const ext = file.name.split('.').pop()?.toLowerCase();
  const mimeFromExt = ext && mimeTypes[ext] ? mimeTypes[ext] : 'application/octet-stream';
  
  console.log('Tipo MIME determinado pela extensão:', mimeFromExt, 'para extensão:', ext);
  return mimeFromExt;
};

/**
 * Valida se um arquivo é uma imagem válida
 *
 * @param file O arquivo a ser validado
 * @returns Promise que resolve para true se válido
 */
export const validateImageFile = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    // Verificar tipo MIME
    if (!file.type.startsWith('image/')) {
      console.error('Arquivo não é uma imagem:', file.type);
      resolve(false);
      return;
    }
    
    // Verificar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      console.error('Arquivo muito grande:', file.size);
      resolve(false);
      return;
    }
    
    // Tentar carregar a imagem para validar se é válida
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      console.log('✅ Arquivo de imagem válido:', {
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      URL.revokeObjectURL(url);
      resolve(true);
    };
    
    img.onerror = () => {
      console.error('❌ Arquivo de imagem inválido ou corrompido');
      URL.revokeObjectURL(url);
      resolve(false);
    };
    
    img.src = url;
  });
};
